import { UtmClassifyAction, UTM_MEDIUM_ALIASES, UTM_MEDIUM_VALUES, UTM_SCHEMA_VERSION, UtmMedium } from './enums';
import { findClid, synthesiseFromClid } from './clid';
import { isLocalHostname, isSabbarHostname, normalizeLlmUtmSource, parseReferrer } from './referrer';
import type { ClassifiedUtmTouch, ClassifyUtmResult, UtmObject } from './types';

function emptyToNull(value: string | null | undefined): string | null {
    if (value == null) return null;
    const trimmed = value.trim();
    return trimmed.length ? trimmed : null;
}

function normalizeUtmValue(value: string | null | undefined): string | null {
    const v = emptyToNull(value);
    // A trailing slash is never meaningful here, and it is easy to end up with one when the
    // campaign params are pasted before the '#' of a hash-router URL.
    return v ? emptyToNull(v.toLowerCase().replace(/\/+$/, '')) : null;
}

export function canonicalUtmHash(touch: Pick<UtmObject, 'utm_source' | 'utm_medium' | 'utm_campaign' | 'utm_content' | 'utm_term' | 'clid' | 'clid_source'>): string {
    return JSON.stringify({
        utm_source: touch.utm_source ?? null,
        utm_medium: touch.utm_medium ?? null,
        utm_campaign: touch.utm_campaign ?? null,
        utm_content: touch.utm_content ?? null,
        utm_term: touch.utm_term ?? null,
        clid: touch.clid ?? null,
        clid_source: touch.clid_source ?? null,
    });
}

function buildTouch(
    fields: Omit<ClassifiedUtmTouch, 'schema_version' | 'captured_at' | 'hash'>,
    capturedAt: string,
): ClassifiedUtmTouch {
    const touch: ClassifiedUtmTouch = {
        schema_version: UTM_SCHEMA_VERSION,
        utm_source: fields.utm_source ?? null,
        utm_medium: fields.utm_medium ?? null,
        utm_campaign: fields.utm_campaign ?? null,
        utm_content: fields.utm_content ?? null,
        utm_term: fields.utm_term ?? null,
        clid: fields.clid ?? null,
        clid_source: fields.clid_source ?? null,
        gclid: fields.clid_source === 'google' ? fields.clid ?? null : null,
        referrer_hostname: fields.referrer_hostname ?? null,
        landing_page: fields.landing_page ?? null,
        confidence: fields.confidence ?? null,
        captured_at: capturedAt,
        hash: '',
    };
    touch.hash = canonicalUtmHash(touch);
    return touch;
}

export function classifySearchParams(
    search: URLSearchParams,
    referrer: string | null | undefined,
    capturedAt: string = new Date().toISOString(),
): ClassifyUtmResult {
    const utmSourceRaw = normalizeUtmValue(search.get('utm_source'));
    let utmMedium = normalizeUtmValue(search.get('utm_medium'));
    const utmCampaign = normalizeUtmValue(search.get('utm_campaign'));
    const utmContent = normalizeUtmValue(search.get('utm_content'));
    const utmTerm = normalizeUtmValue(search.get('utm_term'));
    const hasUtm = Boolean(utmSourceRaw || utmMedium || utmCampaign || utmContent || utmTerm);

    const clidMatch = findClid(search);
    const parsedReferrer = parseReferrer(referrer);
    const referrerHostname = 'hostname' in parsedReferrer ? parsedReferrer.hostname : null;

    if (parsedReferrer.kind === 'internal' && !hasUtm && !clidMatch) {
        // Local ports must not seed a cookie. A Sabbar referrer is kept only as a fallback
        // touch: no source, so an ad, campaign, marketing email, or social visit still wins.
        if (isLocalHostname(parsedReferrer.hostname) || !isSabbarHostname(parsedReferrer.hostname)) {
            return { action: UtmClassifyAction.Ignore, touch: null };
        }
        return {
            action: UtmClassifyAction.Write,
            touch: buildTouch({
                utm_source: null,
                utm_medium: null,
                utm_campaign: null,
                utm_content: null,
                utm_term: null,
                clid: null,
                clid_source: null,
                referrer_hostname: parsedReferrer.hostname,
                confidence: null,
            }, capturedAt),
        };
    }

    if (utmMedium && UTM_MEDIUM_ALIASES[utmMedium]) {
        utmMedium = UTM_MEDIUM_ALIASES[utmMedium];
    }

    if (utmMedium && !UTM_MEDIUM_VALUES.has(utmMedium)) {
        return {
            action: UtmClassifyAction.Reject,
            rejectReason: `utm_medium '${utmMedium}' is not in the allowed list`,
            touch: buildTouch({
                utm_source: utmSourceRaw,
                utm_medium: utmMedium,
                utm_campaign: utmCampaign,
                utm_content: utmContent,
                utm_term: utmTerm,
                clid: clidMatch?.clid ?? null,
                clid_source: clidMatch?.clidSource ?? null,
                referrer_hostname: referrerHostname,
                confidence: null,
            }, capturedAt),
        };
    }

    if (hasUtm) {
        let source = utmSourceRaw;
        let medium = utmMedium;
        let campaign = utmCampaign;
        const llmSource = normalizeLlmUtmSource(source);
        if (llmSource) {
            source = llmSource;
            medium = UtmMedium.AiReferral;
        }
        return {
            action: UtmClassifyAction.Write,
            touch: buildTouch({
                utm_source: source,
                utm_medium: medium,
                utm_campaign: campaign,
                utm_content: utmContent,
                utm_term: utmTerm,
                clid: clidMatch?.clid ?? null,
                clid_source: clidMatch?.clidSource ?? null,
                referrer_hostname: referrerHostname,
                confidence: 'certain',
            }, capturedAt),
        };
    }

    if (clidMatch) {
        const synthesised = synthesiseFromClid(clidMatch, referrerHostname);
        return {
            action: UtmClassifyAction.Write,
            touch: buildTouch({
                utm_source: synthesised.source,
                utm_medium: synthesised.medium,
                utm_campaign: synthesised.campaign,
                utm_content: null,
                utm_term: null,
                clid: clidMatch.clid,
                clid_source: clidMatch.clidSource,
                referrer_hostname: referrerHostname,
                confidence: synthesised.confidence,
            }, capturedAt),
        };
    }

    if (parsedReferrer.kind === 'internal' || parsedReferrer.kind === 'empty' || parsedReferrer.kind === 'unparseable') {
        return {
            action: UtmClassifyAction.Write,
            touch: buildTouch({
                utm_source: null,
                utm_medium: null,
                utm_campaign: null,
                utm_content: null,
                utm_term: null,
                clid: null,
                clid_source: null,
                referrer_hostname: null,
                confidence: null,
            }, capturedAt),
        };
    }

    if (parsedReferrer.kind === 'ai') {
        return {
            action: UtmClassifyAction.Write,
            touch: buildTouch({
                utm_source: parsedReferrer.source,
                utm_medium: UtmMedium.AiReferral,
                utm_campaign: null,
                utm_content: null,
                utm_term: null,
                clid: null,
                clid_source: null,
                referrer_hostname: parsedReferrer.hostname,
                confidence: 'inferred',
            }, capturedAt),
        };
    }

    if (parsedReferrer.kind === 'search') {
        return {
            action: UtmClassifyAction.Write,
            touch: buildTouch({
                utm_source: parsedReferrer.source,
                utm_medium: UtmMedium.Organic,
                utm_campaign: null,
                utm_content: null,
                utm_term: null,
                clid: null,
                clid_source: null,
                referrer_hostname: parsedReferrer.hostname,
                confidence: 'inferred',
            }, capturedAt),
        };
    }

    if (parsedReferrer.kind === 'social') {
        return {
            action: UtmClassifyAction.Write,
            touch: buildTouch({
                utm_source: parsedReferrer.source,
                utm_medium: UtmMedium.OrganicSocial,
                utm_campaign: null,
                utm_content: null,
                utm_term: null,
                clid: null,
                clid_source: null,
                referrer_hostname: parsedReferrer.hostname,
                confidence: 'inferred',
            }, capturedAt),
        };
    }

    return {
        action: UtmClassifyAction.Write,
        touch: buildTouch({
            utm_source: parsedReferrer.source,
            utm_medium: UtmMedium.Referral,
            utm_campaign: null,
            utm_content: null,
            utm_term: null,
            clid: null,
            clid_source: null,
            referrer_hostname: parsedReferrer.hostname,
            confidence: 'inferred',
        }, capturedAt),
    };
}

export function cookiePayloadFromTouch(touch: ClassifiedUtmTouch): UtmObject {
    return {
        schema_version: UTM_SCHEMA_VERSION,
        utm_source: touch.utm_source,
        utm_medium: touch.utm_medium,
        utm_campaign: touch.utm_campaign,
        utm_content: touch.utm_content,
        utm_term: touch.utm_term,
        clid: touch.clid,
        clid_source: touch.clid_source,
        gclid: touch.gclid,
        referrer_hostname: touch.referrer_hostname,
        landing_page: touch.landing_page ?? null,
        captured_at: touch.captured_at,
        created_at: touch.captured_at,
        confidence: touch.confidence,
        hash: touch.hash,
    };
}

export function isNullUtmSource(touch: UtmObject | null | undefined): boolean {
    return !touch?.utm_source;
}

/** A Sabbar page-to-page visit with no campaign. It must not replace a real acquisition touch. */
export function isInternalNavigationTouch(touch: Pick<UtmObject, 'utm_source' | 'utm_medium' | 'utm_campaign' | 'utm_content' | 'utm_term' | 'clid' | 'gclid' | 'referrer_hostname'> | null | undefined): boolean {
    if (!touch || hasUtmSignal(touch)) return false;
    return isSabbarHostname(touch.referrer_hostname);
}

/** True when the touch carries something worth attributing. An all-null touch is a plain direct
 *  visit and must never replace a campaign we already recorded. */
export function hasUtmSignal(touch: UtmObject | null | undefined): boolean {
    if (!touch) return false;
    return Boolean(
        touch.utm_source
        || touch.utm_medium
        || touch.utm_campaign
        || touch.utm_content
        || touch.utm_term
        || touch.clid
        || touch.gclid,
    );
}

/**
 * Under a hash router the campaign params usually end up after the '#', where
 * window.location.search is empty. Merge both query strings, letting the real search string win.
 */
export function searchParamsFromLocation(search: string, hash: string): URLSearchParams {
    const params = new URLSearchParams(search || '');
    const questionMark = (hash || '').indexOf('?');
    if (questionMark === -1) return params;
    new URLSearchParams(hash.substring(questionMark + 1)).forEach((value, key) => {
        if (!params.has(key)) params.set(key, value);
    });
    return params;
}

/** Query string before the hash and/or after the HashRouter route. */
export function getPageSearchParams(): URLSearchParams {
    if (typeof window === 'undefined') return new URLSearchParams();
    return searchParamsFromLocation(window.location.search, window.location.hash);
}

export function isTransactionalMedium(medium: string | null | undefined): boolean {
    return medium === UtmMedium.Transactional;
}

export function isSameUtmTouch(a: UtmObject | null | undefined, b: UtmObject | null | undefined): boolean {
    if (!a && !b) return true;
    if (!a || !b) return false;
    const hashA = a.hash || canonicalUtmHash(a);
    const hashB = b.hash || canonicalUtmHash(b);
    return hashA === hashB;
}
